import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardBody, Col, Container, Row, Button, Form, Alert, ListGroup, InputGroup, Table, Modal, Spinner } from 'react-bootstrap';
import { FaUserPlus } from 'react-icons/fa'; // Example icon
import moment from 'moment'; // Still useful for date display, but proration calculation is now API-driven

// Assuming these components/utilities exist in your project
import PlanHeader from '../../../Components/settings/PlansAndPricingComponent/CreatePlan/ValidationForm/PlanHeader';
import axiosInstance from '../../../Utils/axiosInstance';
import { backendbaseUrl } from '../../../Utils/baseUrl';
import { useNavigate } from 'react-router-dom';

// --- CSS Styles Embedded (Adjust to match Yuri theme exactly) ---
const styles = `
.yuri-card {
  border-radius: 8px;
  overflow: hidden; /* For shadow clipping */
  transition: all 0.3s ease-in-out;
  margin-bottom: 1.5rem; /* Ensure spacing when stacked on smaller screens */
}

.yuri-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08) !important; /* Emphasize on hover */
}import { toInteger } from 'lodash';
import { Success } from './../../../Utils/Constants/index';


.clickable-card {
  cursor: pointer;
  border: 1px solid #e0e0e0;
}

.clickable-card:hover {
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideInRight {
  from { transform: translateX(20px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes fadeInUp {
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.animated-fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}

.animated-slide-in-right {
  animation: slideInRight 0.8s ease-out forwards;
}

.animated-fade-in-up {
  animation: fadeInUp 0.8s ease-out forwards;
}

.animated-slide-in-right {
  animation-delay: 0.2s;
}

.animated-fade-in-up {
  animation-delay: 0.4s;
}
`;

// Inject styles into the head of the document
if (typeof window !== 'undefined') {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);
}

// --- Your Provided API Interfaces ---
export interface Organization {
    id: string;
    name: string;
    email: string;
    is_plan_active: boolean;
}

export interface Plans { // This is the 'plan' object inside plan_details
    id: string;
    name: string;
    days: number;
    max_users: number;
}

export interface PlanDetails {
    id: string;
    organization: Organization;
    plan: Plans;
    purchase_date: string;       // Format: DD-MM-YYYY
    expiry_date: string;         // Format: DD-MM-YYYY
    users_under_plan: string;    // e.g. "8/1"
    is_online_purchase: boolean;
    admin_approved: 'approved' | 'pending' | 'rejected';
    created_at: number;          // Unix timestamp
    is_plan_active: boolean;
    used_user: number;
}

export interface AppDetails { // This represents a single app/plan
    id: string;
    selected_apps: string[]; // List of app names/IDs
    ordering_id: number;
    created_at: number;
    retrieved_at: number | null;
    name: string;
    description: string; // Often used for features or details
    price: string;               // e.g. "3.00"
    days: number;
    max_users: number;
    visibility: boolean;
    custom_plan: boolean;
    country: string;             // UUID string of country
    proration_plan: any;         // You might want to define this type more specifically
    currency: string;            // Added based on your proration response
}

// Updated PlanResponse based on your latest sample data
// 'apps' is a single object, not an array
export interface CurrentPlanResponse {
    plan_details: PlanDetails | null;
    apps: AppDetails | null; // This is for the CURRENT plan's associated app details
}

// Interface for the API response that lists AVAILABLE upgrade plans
// Assuming another endpoint will return an array of AppDetails
export interface AvailablePlansResponse {
    available_plans: AppDetails[]; // Assuming this is the structure for available plans
}

// NEW: Proration Calculation API Interfaces
export interface ProrationCalculationRequest {
    user_count: number;
}

export interface ProrationData {
    remaining_days: number;
    single_user_cost: number;
    single_user_per_day_cost: number;
    final_cost: number; // This is the value we need!
    price_of_the_plan: number;
    max_user: number;
    days: number;
    currency: string;
    total_cost: number;
}

export interface ProrationCalculationResponse {
    data: ProrationData;
}


// --- UI Specific Interfaces (Mapped from API) ---
// This UIPlan interface will be used for both current plan representation
// and available upgrade plans, making it easier to manage state.
interface UIPlan {
    id: string;
    name: string;
    purchaseDate?: string; // For current plan (DD-MM-YYYY)
    expiryDate?: string;   // For current plan (DD-MM-YYYY)
    currentLimit?: number; // For current plan (from plan_details.plan.max_users)
    usedUsers?: number;    // For current plan (from plan_details.used_user)
    limit: number;         // For upgrade plans (from app.max_users)
    features: string[];    // Derived from app.description or other source
    price: number;         // From app.price, converted to number
    description?: string;  // From app.description
    days: number;          // From app.days or plan.days
    currency: string;      // Currency for display
}

interface CurrentPlanDetailsProps {
    plan: UIPlan;
}

interface UpgradeOptionsProps {
    availableUpgradePlans: UIPlan[];
    selectedPlan: UIPlan | null;
    handlePlanSelect: (plan: UIPlan) => void;
    customUserLimit: string;
    handleCustomLimitChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    currentUserLimit: number;
    prorationLoading: boolean; // Pass proration loading to disable fields
}

interface UpgradeSummaryProps {
    selectedPlan: UIPlan | null;
    customUserLimit: string;
    currentUserPlan: UIPlan;
    proratedAmount: string;
    prorationLoading: boolean;
    prorationError: string | null;
    prorationCalculation: ProrationCalculationResponse | null
}

interface UpgradeConfirmationModalProps {
    show: boolean;
    onHide: () => void;
    onConfirm: () => void;
    selectedPlan: UIPlan | null;
    customUserLimit: string;
    proratedAmount: string;
    currency: string;
}

// --- CurrentPlanDetails Component ---
const CurrentPlanDetails: React.FC<CurrentPlanDetailsProps> = ({ plan }) => {
    return (
        <>
            <h4 className="mb-4 text-primary">Your Current Plan</h4>
            <p className="lead fw-bold mb-1">{plan.name}</p>
            {plan.purchaseDate && <p className="text-muted mb-1">Purchased on: {plan.purchaseDate}</p>}
            {plan.expiryDate && <p className="text-muted mb-3">Expires on: {plan.expiryDate}</p>}

            <ListGroup variant="flush">
                {plan.currentLimit !== undefined && (
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        Current User Limit:
                        <span className="badge bg-primary rounded-pill fs-5">{plan.currentLimit}</span>
                    </ListGroup.Item>
                )}
                {plan.usedUsers !== undefined && (
                    <ListGroup.Item className="d-flex justify-content-between align-items-center">
                        Users Currently Used:
                        <span className="badge bg-info rounded-pill fs-5">{plan.usedUsers}</span>
                    </ListGroup.Item>
                )}
                <ListGroup.Item>
                    <h6 className="mt-3 mb-2">Key Features:</h6>
                    {plan.features.length > 0 ? (
                        <ul>
                            {plan.features.map((feature, index) => (
                                <li key={index} className="text-muted mb-1">{feature}</li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-muted">No specific features listed.</p>
                    )}
                </ListGroup.Item>
            </ListGroup>
        </>
    );
};

// --- UpgradeOptions Component ---
const UpgradeOptions: React.FC<UpgradeOptionsProps> = ({
    availableUpgradePlans,
    selectedPlan,
    handlePlanSelect,
    customUserLimit,
    handleCustomLimitChange,
    currentUserLimit,
    prorationLoading, // Use this to disable
}) => {
    return (
        <>
            {/* <h4 className="mb-4">Choose Your Upgrade Path</h4>

            {availableUpgradePlans.length > 0 ? (
                availableUpgradePlans.map((plan) => (
                    <Card
                        key={plan.id}
                        className={`mb-3 clickable-card ${selectedPlan?.id === plan.id ? 'border-primary shadow-lg' : ''}`}
                        onClick={() => handlePlanSelect(plan)}
                        // Disable cards if proration is loading to prevent rapid recalculations
                        style={{ pointerEvents: prorationLoading ? 'none' : 'auto', opacity: prorationLoading ? 0.6 : 1 }}
                    >
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h5 className="mb-1">{plan.name} <span className="badge bg-info ms-2">{plan.limit} Users</span></h5>
                                    {plan.description && <p className="text-muted mb-0">{plan.description}</p>}
                                </div>
                                <h4 className="text-primary">{plan.currency}{plan.price.toFixed(2)}</h4>
                            </div>
                            {plan.features.length > 0 && (
                                <ul className="list-unstyled mt-2 text-muted small">
                                    {plan.features.slice(0, 3).map((feature, index) => (
                                        <li key={index}><i className="ri-check-line text-success me-1"></i> {feature}</li>
                                    ))}
                                    {plan.features.length > 3 && <li>...and more!</li>}
                                </ul>
                            )}
                        </CardBody>
                    </Card>
                ))
            ) : (
                <Alert variant="info">No upgrade plans available at this time or all plans are below your current limit.</Alert>
            )} */}

            <hr className="my-4" />

            <h5 className="mb-3">How Many more Users You want to add</h5>
            <InputGroup className="mb-3">
                <InputGroup.Text><FaUserPlus /></InputGroup.Text>
                <Form.Control
                    type="number"
                    placeholder={`Enter new user limit (e.g., ${currentUserLimit + 10})`}
                    value={customUserLimit}
                    onChange={handleCustomLimitChange}
                // min={currentUserLimit + 1}
                // isInvalid={customUserLimit !== '' && parseInt(customUserLimit) <= currentUserLimit}
                // disabled={prorationLoading} // Disable input while proration is loading
                />
                <Form.Control.Feedback type="invalid">
                    Must be greater than your current limit of {currentUserLimit}.
                </Form.Control.Feedback>
            </InputGroup>
            <small className="text-muted">You will be prorated based on the selected or custom limit.</small>
        </>
    );
};

// --- UpgradeSummary Component ---
const UpgradeSummary: React.FC<UpgradeSummaryProps> = ({ selectedPlan, customUserLimit, currentUserPlan, proratedAmount, prorationLoading, prorationError, prorationCalculation }) => {
    const isCustom = customUserLimit !== '' && !selectedPlan;

    return (
        <>
            <h4 className="mb-4">Upgrade Summary & Proration</h4>
            <Table striped bordered hover responsive className="animated-fade-in-up">
                <thead>
                    <tr>
                        <th>Detail</th>
                        <th>Current</th>
                        <th>New</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>Plan Name</td>
                        <td>{currentUserPlan.name}</td>
                        <td>{isCustom ? 'Custom Limit' : selectedPlan?.name || 'N/A'}</td>
                    </tr>
                    <tr>
                        <td>User Limit</td>
                        <td>{currentUserPlan.currentLimit}</td>
                        <td>
                            {isCustom ? (
                                <>
                                    <span>{currentUserPlan.currentLimit}</span> +{' '}
                                    <span
                                        style={{
                                            fontWeight: 'bold',
                                            backgroundColor: '#d4edda', // soft green background
                                            color: '#155724',           // dark green text for contrast
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        {parseInt(customUserLimit, 10)}
                                    </span>
                                </>
                            ) : (
                                selectedPlan?.limit !== undefined ? selectedPlan.limit : 'N/A'
                            )}
                        </td>
                    </tr>
                    <tr>
                        <td>Base Price ({currentUserPlan?.days === 365 ? 'Annual' : 'Other'})</td>
                        <td>{currentUserPlan?.currency} {currentUserPlan.price.toFixed(2)}</td>

                        <td>{isCustom ? `Cost of  ${customUserLimit} user For the Remaining : ${prorationCalculation?.data.remaining_days} Days` : `${selectedPlan?.currency || ''} ${selectedPlan?.price.toFixed(2) || 'N/A'}`}</td>
                    </tr>
                </tbody>
            </Table>

            <div className="mt-4 p-3 bg-light rounded shadow-sm">
                <h5 className="mb-3">Proration Logic Explained:</h5>
                <p className="text-muted">
                    When you upgrade, we calculate the remaining value of your current plan and apply it towards the cost of your new plan.
                    You'll only pay the difference for the upgraded service for the remainder of your current billing cycle.
                </p>
                <p className="lead mb-0 text-success fw-bold d-flex justify-content-between align-items-center">
                    Estimated Prorated Amount Due:
                    {prorationLoading ? (
                        <Spinner animation="border" size="sm" className="ms-2" />
                    ) : prorationError ? (
                        <span className="text-danger">Error</span>
                    ) : (
                        <span className="fs-3">{currentUserPlan?.currency || '$'}{Number(customUserLimit) * Number(proratedAmount)}</span>
                    )}
                </p>
                {prorationError && <Alert variant="danger" className="mt-2 p-2">{prorationError}</Alert>}
                <small className="text-info">
                    This is an estimated amount. The final charge will be displayed before confirmation.
                </small>
            </div>
        </>
    );
};


// --- UpgradeConfirmationModal Component ---
const UpgradeConfirmationModal: React.FC<UpgradeConfirmationModalProps> = ({ show, onHide, onConfirm, selectedPlan, customUserLimit, proratedAmount, currency }) => {
    const isCustom = customUserLimit !== '' && !selectedPlan;
    const [loading, setLoading] = useState(false);
    const handleConfirm = async () => {
        try {
            setLoading(true);
            await onConfirm();
        } catch (err) {
            console.error("Upgrade failed in modal:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Confirm Your Upgrade</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>You are about to upgrade your plan to:</p>
                <h5 className="text-primary">
                    {isCustom ? `Custom Limit: ${customUserLimit} Users` : selectedPlan?.name || 'N/A Plan'}
                </h5>
                {selectedPlan && (
                    <p className="text-muted">New User Limit: {selectedPlan.limit}</p>
                )}
                <p className="lead mt-3">
                    Estimated Prorated Amount Due: <span className="text-success fw-bold">{currency}{Number(customUserLimit) * Number(proratedAmount)}</span>
                </p>
                <p>Are you sure you want to proceed with this upgrade?</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancel
                </Button>

                <Button variant="primary" onClick={handleConfirm} disabled={loading}>
                    {loading ? (
                        <>
                            <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                            />{" "}
                            Processing...
                        </>
                    ) : (
                        "Confirm Upgrade"
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

// --- Main UpgradeUser Component ---
const UpgradeUser: React.FC = () => {
    const [currentUserPlan, setCurrentUserPlan] = useState<UIPlan | null>(null);
    const [availableUpgradePlans, setAvailableUpgradePlans] = useState<UIPlan[]>([]);
    const [selectedPlan, setSelectedPlan] = useState<UIPlan | null>(null);
    const [customUserLimit, setCustomUserLimit] = useState<string>('');
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [upgradeError, setUpgradeError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [fetchError, setFetchError] = useState<string | null>(null);

    // New state for proration calculation
    const [proratedAmount, setProratedAmount] = useState<string>('0.00');
    const [prorationCalculation, setProrationCalculation] = useState<ProrationCalculationResponse | null>(null)
    const [prorationLoading, setProrationLoading] = useState<boolean>(false);
    const [prorationError, setProrationError] = useState<string | null>(null);

    // Function to fetch organization plan details (current plan)
    const fetchCurrentPlanData = useCallback(async () => {
        setLoading(true);
        setFetchError(null);
        try {
            const response = await axiosInstance.get<CurrentPlanResponse>(`${backendbaseUrl}/company/plan/plan-details`);
            const data = response.data;

            if (data.plan_details && data.apps) { // Ensure both plan_details and apps for current plan are present
                const currentPlanDetail = data.plan_details;
                const currentAppDetails = data.apps;

                const currentUIPlan: UIPlan = {
                    id: currentPlanDetail.id, // Or currentAppDetails.id if that's the primary ID for upgrade
                    name: currentPlanDetail.plan.name,
                    purchaseDate: currentPlanDetail.purchase_date,
                    expiryDate: currentPlanDetail.expiry_date.trim(),
                    currentLimit: currentPlanDetail.plan.max_users,
                    usedUsers: currentPlanDetail.used_user,
                    limit: currentAppDetails.max_users, // Using app.max_users for limit property
                    features: currentAppDetails.description ? [currentAppDetails.description] : ["Base features included with your current plan"],
                    price: parseFloat(currentAppDetails.price),
                    currency: currentAppDetails.currency, // Capture currency
                    days: currentAppDetails.days, // Use days from AppDetails for consistency in UIPlan
                };
                setCurrentUserPlan(currentUIPlan);
            } else {
                setFetchError("Could not retrieve current plan details or associated app information.");
                setCurrentUserPlan(null);
            }

        } catch (err: any) {
            console.error("Failed to fetch current plan data:", err);
            setFetchError(err.response?.data?.message || "Failed to load current plan details. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    // Function to fetch available upgrade plans
    // YOU NEED TO REPLACE THIS ENDPOINT WITH YOUR ACTUAL API ENDPOINT
    // THAT LISTS ALL AVAILABLE PLANS FOR UPGRADE
    const fetchAvailableUpgradePlans = useCallback(async (currentMaxUsers: number) => {
        try {
            // Simulate API call for available plans. Replace with your actual endpoint.
            // Example: await axiosInstance.get<AvailablePlansResponse>(`${backendbaseUrl}/company/plans/available-for-upgrade`);
            // For now, I'll use a hardcoded list for demonstration.
            const response = await new Promise<AvailablePlansResponse>((resolve) => {
                setTimeout(() => {
                    resolve({
                        available_plans: [
                            {
                                id: 'business-plan-id',
                                selected_apps: ["Paper Trading", "Accounting", "Risk Calculator", "Settings", "Reports"],
                                ordering_id: 25,
                                created_at: 1746000000,
                                retrieved_at: null,
                                name: "Business Tier",
                                description: "Enhanced features for growing teams, including advanced reporting and dedicated support.",
                                price: "199.00", // Example price for a monthly/annual plan
                                days: 365, // Example: Annual plan
                                max_users: 50,
                                visibility: true,
                                custom_plan: false,
                                country: "f726eb75-ac2e-45cd-9d22-c3d870e0054f",
                                proration_plan: null,
                                currency: "$"
                            },
                            {
                                id: 'enterprise-plan-id',
                                selected_apps: ["Paper Trading", "Accounting", "Risk Calculator", "Settings", "Reports", "Custom Integrations"],
                                ordering_id: 26,
                                created_at: 1746050000,
                                retrieved_at: null,
                                name: "Enterprise Solution",
                                description: "Full-suite solution for large enterprises, with custom integrations, SSO, and premium support.",
                                price: "499.00",
                                days: 365,
                                max_users: 200,
                                visibility: true,
                                custom_plan: false,
                                country: "f726eb75-ac2e-45cd-9d22-c3d870e0054f",
                                proration_plan: null,
                                currency: "$"
                            },
                            {
                                id: 'small-upgrade-plan-id',
                                selected_apps: [],
                                ordering_id: 27,
                                created_at: 1746050000,
                                retrieved_at: null,
                                name: "Small Team Boost",
                                description: "Get a few more users to expand your team.",
                                price: "25.00",
                                days: 30, // Monthly plan
                                max_users: 10,
                                visibility: true,
                                custom_plan: false,
                                country: "f726eb75-ac2e-45cd-9d22-c3d870e0054f",
                                proration_plan: null,
                                currency: "$"
                            }
                        ].filter(app => app.max_users > currentMaxUsers) // Filter plans that offer more users than current
                    });
                }, 500); // Simulate network delay
            });


            const upgradePlans: UIPlan[] = response.available_plans.map(app => ({
                id: app.id,
                name: app.name,
                limit: app.max_users,
                price: parseFloat(app.price),
                description: app.description,
                features: app.description ? app.description.split(', ').map(f => f.trim()) : app.selected_apps, // Or parse from description or use selected_apps
                days: app.days, // Crucial for proration
                currency: app.currency
            }));
            setAvailableUpgradePlans(upgradePlans);

        } catch (err: any) {
            console.error("Failed to fetch available upgrade plans:", err);
            // setFetchError(err.response?.data?.message || "Failed to load upgrade options."); // Don't block if current plan loaded
        }
    }, []);

    // Main data fetching effect
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            setFetchError(null);
            try {
                const currentPlanRes = await axiosInstance.get<CurrentPlanResponse>(`${backendbaseUrl}/company/plan/plan-details`);
                const currentPlanData = currentPlanRes.data;

                if (currentPlanData.plan_details && currentPlanData.apps) {
                    const currentPlanDetail = currentPlanData.plan_details;
                    const currentAppDetails = currentPlanData.apps;

                    const currentUIPlan: UIPlan = {
                        id: currentPlanDetail.id,
                        name: currentPlanDetail.plan.name,
                        purchaseDate: currentPlanDetail.purchase_date,
                        expiryDate: currentPlanDetail.expiry_date.trim(),
                        currentLimit: currentPlanDetail.plan.max_users,
                        usedUsers: currentPlanDetail.used_user,
                        limit: currentAppDetails.max_users,
                        features: currentAppDetails.description ? [currentAppDetails.description] : ["Base features"],
                        price: parseFloat(currentAppDetails.price), // Price of current plan
                        days: currentAppDetails.days, // Days of current plan
                        currency: currentAppDetails.currency,
                    };
                    setCurrentUserPlan(currentUIPlan);

                    // Now fetch available upgrade plans based on the current user's limit
                    await fetchAvailableUpgradePlans(currentPlanDetail.plan.max_users);
                } else {
                    setFetchError("Could not retrieve current plan details or associated app information.");
                    setCurrentUserPlan(null);
                }
            } catch (err: any) {
                console.error("Error loading plan data:", err);
                setFetchError(err.response?.data?.message || "Failed to load plan details. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [fetchAvailableUpgradePlans]); // Dependency on fetchAvailableUpgradePlans


    // NEW: Effect to calculate proration whenever selectedPlan or customUserLimit changes
    useEffect(() => {
        const getProratedAmount = async () => {
            setProrationLoading(true);
            setProrationError(null);
            setProratedAmount('0.00'); // Reset amount while loading

            let targetUserCount: number | null = null;

            if (selectedPlan) {
                targetUserCount = selectedPlan.limit;
            } else if (customUserLimit !== '' && currentUserPlan) {
                targetUserCount = parseInt(customUserLimit);
            }

            if (targetUserCount === null || isNaN(targetUserCount)) {
                setProrationLoading(false);
                // Only clear proration details if no valid selection/input
                if (!selectedPlan && customUserLimit === '') {
                    setProratedAmount('0.00');
                    setProrationError(null);
                }
                return;
            }

            try {
                const requestBody: ProrationCalculationRequest = {
                    user_count: targetUserCount,
                    // You might need to add other fields to the request if your backend's proration
                    // depends on the specific plan being upgraded to (e.g., plan_id)
                    // For now, based on your request, only user_count is explicitly required.
                };

                const response = await axiosInstance.post<ProrationCalculationResponse>(
                    `${backendbaseUrl}/company/plan/proration-calculation`,
                    requestBody
                );

                if (response.data && response.data.data && typeof response.data.data.final_cost === 'number') {
                    setProratedAmount(response.data.data.final_cost.toFixed(2));
                    setProrationCalculation(response.data)
                } else {
                    setProrationError("Proration calculation failed: Invalid response from API.");
                    setProratedAmount('0.00');
                }
            } catch (err: any) {
                console.error("Proration API error:", err);
                setProrationError(err.response?.data?.message || "Failed to calculate proration. Please try again.");
                setProratedAmount('0.00');
            } finally {
                setProrationLoading(false);
            }
        };

        getProratedAmount()

        // // Trigger proration calculation only if a selection is made or custom limit is valid
        // if (selectedPlan || (customUserLimit !== '')) {
        //     getProratedAmount();
        // } else {
        //     setProratedAmount('0.00');
        //     setProrationError(null);
        //     setProrationLoading(false);
        // }

    }, [selectedPlan, customUserLimit, currentUserPlan]); // Dependencies for proration calculation


    const handlePlanSelect = (plan: UIPlan) => {
        setSelectedPlan(plan);
        setCustomUserLimit('');
        setUpgradeError('');
    };

    const handleCustomLimitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const numValue = parseInt(value);
        setCustomUserLimit(value);


    };

    const handleUpgradeClick = () => {
        if (!currentUserPlan || currentUserPlan.currentLimit === undefined) {
            setUpgradeError("Current plan details not loaded. Please wait or refresh.");
            return;
        }
        if (prorationLoading) {
            setUpgradeError("Proration calculation is in progress. Please wait.");
            return;
        }
        if (prorationError) {
            setUpgradeError("Cannot proceed due to proration calculation error. Please resolve the issue.");
            return;
        }

        if (selectedPlan || (customUserLimit !== '')) {
            setUpgradeError('');
            setShowConfirmationModal(true);
        } else {
            setUpgradeError('Please select an upgrade plan or enter a valid custom user limit.');
        }
    };

    const [trackId, SetTrackId] = useState<string>("")
    const navigate = useNavigate()
    const confirmUpgrade = async (loadingState: any) => {

        setUpgradeError('');

        try {
            let upgradePayload: any;
            let newMaxUsers = 0;

            if (selectedPlan) {
                newMaxUsers = selectedPlan.limit;
                upgradePayload = {
                    plan_id: selectedPlan.id, // ID of the selected app/plan
                    new_max_users: selectedPlan.limit,
                    // Add any other fields your backend expects, like `original_plan_id` etc.
                };
            } else if (customUserLimit !== '') {
                newMaxUsers = parseInt(customUserLimit);
                upgradePayload = {
                    custom_limit: parseInt(customUserLimit),
                    // You might need to send a `base_plan_id` if custom limits are tied to a base plan
                };
            } else {
                setUpgradeError("No plan selected for upgrade.");
                return;
            }


            // Potentially, you could include the `proratedAmount` in the payload as well
            // if your backend requires it for final verification or record-keeping.
            // upgradePayload.final_prorated_cost = parseFloat(proratedAmount);


            const paymentResponse = await axiosInstance.get(`${backendbaseUrl}/company/plan/proration-calculation/payment/uuid`)
            console.log("Full payment response:", paymentResponse);
            if (paymentResponse.status === 200 && paymentResponse.data?.data?.track_id) {
                const trackId = paymentResponse.data.data.track_id;

                // SetTrackId(trackId); // Optional: also store in state for later use

                // Step 2: Make second request with trackId
                const baseUrl = window.location.origin;
                const response = await axiosInstance.post(`${backendbaseUrl}/company/plan/proration-calculation/payment/`, {
                    user_count: customUserLimit,
                    success_url: `${baseUrl}/user-upgrade/payment-success/verification/${trackId}`,
                    cancel_url: `${baseUrl}/user-upgrade/payment-failed/verification/${trackId}`,
                    track_id: trackId,
                });

                console.log(response)
                if (response.status == 200) {
                    window.location.href = response.data.checkout_url; // 🔥 Redirect to Stripe Checkout

                } else {
                    setUpgradeError(response.data.message || "Upgrade failed. Please try again.");
                }
            } else {
                setUpgradeError(paymentResponse.data.message || "Upgrade failed. Please try again.");
            }
        } catch (err: any) {
            console.error("Upgrade API error:", err);
            setUpgradeError(err.response?.data?.message || "An error occurred during upgrade. Please try again.");
        }
    };

    if (loading) {
        return (
            <div className="page-body d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p className="ms-2">Loading plan details...</p>
            </div>
        );
    }

    if (fetchError) {
        return (
            <div className="page-body">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <PlanHeader headClass='pb-4 card' title={"Upgrade User Limit"} />
                            <Alert variant="danger" className="mt-4 animated-fade-in">
                                <h4>Error Loading Data!</h4>
                                <p>{fetchError}</p>
                                <Button onClick={fetchCurrentPlanData} variant="danger">Retry</Button>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    if (!currentUserPlan) {
        return (
            <div className="page-body">
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <PlanHeader headClass='pb-4 card' title={"Upgrade User Limit"} />
                            <Alert variant="warning" className="mt-4 animated-fade-in">
                                <h4>No Active Plan Found!</h4>
                                <p>It seems your organization doesn't have an active plan. Please contact support or purchase a new plan.</p>
                            </Alert>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    // Determine if the "Proceed to Upgrade" button should be disabled
    const isProceedButtonDisabled = prorationLoading || proratedAmount === '0.00' || !!prorationError || (!selectedPlan && (customUserLimit === ''));


    return (
        <div className="page-body">
            <Container fluid>
                <Row>
                    <Col lg={12}>
                        <PlanHeader headClass='pb-4 card' title={"Upgrade User Limit"} />
                    </Col>
                </Row>

                <Row className="mt-4">
                    <Col lg={6}>
                        <Card className="shadow-sm yuri-card animated-fade-in">
                            <CardBody>
                                <CurrentPlanDetails plan={currentUserPlan} />
                            </CardBody>
                        </Card>
                    </Col>

                    <Col lg={6}>
                        <Card className="shadow-sm yuri-card animated-slide-in-right">
                            <CardBody>
                                <UpgradeOptions
                                    availableUpgradePlans={availableUpgradePlans}
                                    selectedPlan={selectedPlan}
                                    handlePlanSelect={handlePlanSelect}
                                    customUserLimit={customUserLimit}
                                    handleCustomLimitChange={handleCustomLimitChange}
                                    currentUserLimit={currentUserPlan.currentLimit!}
                                    prorationLoading={prorationLoading} // Pass loading state
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Show summary only if a plan is selected or a valid custom limit is entered */}
                {((customUserLimit !== '')) && (
                    <Row className="mt-4 animated-fade-in-up">
                        <Col lg={12}>
                            <Card className="shadow-sm yuri-card">
                                <CardBody>
                                    <UpgradeSummary
                                        selectedPlan={selectedPlan}
                                        customUserLimit={customUserLimit}
                                        currentUserPlan={currentUserPlan}
                                        proratedAmount={proratedAmount} // Use state variable
                                        prorationLoading={prorationLoading}
                                        prorationError={prorationError}
                                        prorationCalculation={prorationCalculation}
                                    />
                                    {upgradeError && <Alert variant="danger" className="mt-3">{upgradeError}</Alert>}
                                    <div className="d-flex justify-content-end mt-4">
                                        <Button
                                            variant="primary"
                                            size="lg"
                                            onClick={handleUpgradeClick}
                                            disabled={isProceedButtonDisabled} // Disable button based on proration state
                                        >
                                            Proceed to Upgrade
                                        </Button>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}

                {upgradeError && !selectedPlan && !(customUserLimit !== '') && (
                    <Row className="mt-4">
                        <Col lg={12}>
                            <Alert variant="danger">{upgradeError}</Alert>
                        </Col>
                    </Row>
                )}

                <UpgradeConfirmationModal
                    show={showConfirmationModal}
                    onHide={() => setShowConfirmationModal(false)}
                    onConfirm={() => confirmUpgrade(true)}
                    selectedPlan={selectedPlan}
                    customUserLimit={customUserLimit}
                    proratedAmount={proratedAmount}
                    currency={currentUserPlan.currency || '$'}
                />
            </Container>
        </div>
    );
};

export default UpgradeUser;